import numeral from 'numeral';

import type { IChannelForecast, IForecast, TAdChannelKey } from '@feathr/blackbox';

/**
 * Builds the channels array from channel visibility flags.
 *
 * When `isMetaV2` is false, returns `undefined` to trigger legacy (non-channel)
 * forecast behavior. When `isMetaV2` is true, builds an array of enabled channels.
 * Returns `undefined` if no channels are enabled.
 *
 * @param showDisplay - Whether the Display Ads channel is enabled.
 * @param showMeta - Whether the Meta Ads channel is enabled.
 * @param isMetaV2 - Whether the metaV2 feature flag is on. When false, returns
 *   undefined to trigger legacy (non-channel) behavior.
 * @returns Array of forecast channels to request, or undefined for legacy behavior.
 * @example
 * buildChannelsArray(true, true, true);  // ['tradedesk', 'meta']
 * buildChannelsArray(true, false, true); // ['tradedesk']
 * buildChannelsArray(false, false, true); // undefined
 * buildChannelsArray(true, true, false); // undefined
 */
function buildChannelsArray(
  showDisplay: boolean,
  showMeta: boolean,
  isMetaV2: boolean,
): TAdChannelKey[] | undefined {
  if (!isMetaV2) {
    return undefined;
  }

  const channels: TAdChannelKey[] = [];

  if (showDisplay) {
    channels.push('tradedesk');
  }

  if (showMeta) {
    channels.push('meta');
  }

  return channels.length > 0 ? channels : undefined;
}

/**
 * Formats a reach number for display with a tilde prefix and comma separators.
 *
 * @param reach - The reach number to format.
 * @returns Formatted string like "~45,000" or "~500".
 * @example
 * formatReachEstimate(45000); // "~45,000"
 * formatReachEstimate(500);   // "~500"
 * formatReachEstimate(0);     // "~0"
 */
function formatReachEstimate(reach: number): string {
  return `~${numeral(reach).format('0,0')}`;
}

/**
 * Parses a legacy forecast response (no channels) into per-channel reach data.
 *
 * Used when `metaV2` is OFF. Extracts display reach from `response.min`,
 * defaulting to 0 if `min` is not present. Meta reach is always null since
 * the legacy endpoint does not provide per-channel data.
 *
 * @param response - Legacy `{ min, max }` response from `/segments/forecast`.
 * @returns Object with `displayReach` from min and `metaReach` as null.
 * @example
 * parseLegacyForecast({ min: 5000, max: 10000 }); // { displayReach: 5000, metaReach: null }
 */
function parseLegacyForecast(response: IForecast): { displayReach: number; metaReach: null } {
  return {
    displayReach: response.min ?? 0,
    metaReach: null,
  };
}

/**
 * Parses a channel forecast response into per-channel reach data.
 *
 * Uses `min` (lower bound) for both channels since Meta's upper bound is
 * unreliable (per issue #7508: "FE should use min for the displayed estimate").
 *
 * @param response - Per-channel response with optional `tradedesk` and `meta` entries.
 * @returns Object with `displayReach` from `tradedesk.min` and `metaReach` from `meta.min`.
 *   Either value is null if the corresponding channel is absent from the response.
 * @example
 * parseChannelForecast({ tradedesk: { min: 3000, max: 6000 }, meta: { min: 1500, max: 4000 } });
 * // { displayReach: 3000, metaReach: 1500 }
 */
function parseChannelForecast(response: IChannelForecast): {
  displayReach: number | null;
  metaReach: number | null;
} {
  return {
    displayReach: response.tradedesk?.min ?? null,
    metaReach: response.meta?.min ?? null,
  };
}

/**
 * Type guard that detects whether a forecast response is channel-based or legacy.
 *
 * Acts as a safety net for when `metaV2` is ON but the backend hasn't deployed
 * the channel-aware endpoint yet. In that case, the backend returns the legacy
 * `{ min, max }` shape even when `channels` is sent in the request.
 *
 * @param response - The raw response from the forecast endpoint.
 * @returns `true` if the response contains channel keys (`tradedesk` or `meta`),
 *   indicating it is an `IChannelForecast`.
 * @example
 * isChannelForecastResponse({ tradedesk: { min: 100, max: 200 } }); // true
 * isChannelForecastResponse({ min: 5000, max: 10000 });              // false
 */
function isChannelForecastResponse(
  response: IForecast | IChannelForecast,
): response is IChannelForecast {
  return 'tradedesk' in response || 'meta' in response;
}

export {
  buildChannelsArray,
  formatReachEstimate,
  isChannelForecastResponse,
  parseChannelForecast,
  parseLegacyForecast,
};
